/**
 * List Viewer (piece 4): the one reusable table. Column roles and types drive
 * alignment and formatting; every sort is total with the record id as the
 * final tiebreaker; pages are 50, 100 or 200 rows and never a virtualized
 * scroll; the selection is a page's ids or the view's filter plus exclusions
 * (decision 0244); Compact mode switches on by measured width below the
 * 480px token (decision 0259); groups keep the caller's explicit order.
 */
export type ColumnRole = 'date' | 'primary' | 'secondary' | 'status' | 'value';
/**
 * 🔒 `date` is a calendar day, drawn as the day it names; `instant` is a moment,
 * drawn on the viewer's own day (decision 1260). A created, sent or posted time
 * is an instant, so a row raised at 8 PM Eastern reads as that evening's day.
 *
 * 🔒 `pills` is one cell holding several words, drawn as one pill per word (T752;
 * decision 1486): the Companies list's Kind, a company's flags. The row carries
 * the words joined by a comma and a space, so a saved view's criteria, the CSV
 * export and the compact line read the same text the pills draw.
 *
 * 🔒 `image` is a cell holding a file id, drawn as the picture at the row's height from the file route, a neutral
 * placeholder where it is empty (T3333; decision 9380). It has no text: the compact line, the export and a pane leave it out.
 *
 * 🔒 `marks` is a row's marks as words joined by a comma and a space (T3164; decision 9720): the gold suggestions dot and
 * a missing tax form's triangle. The export and a view's conditions read the words; the list draws the caller's glyphs in
 * their place, at the far left after the checkbox, and the compact line's secondary text leaves it out.
 */
export type ColumnType = 'text' | 'date' | 'instant' | 'status' | 'currency' | 'number' | 'link' | 'pills' | 'image' | 'marks';

export interface ColumnDef {
  key: string;
  label: string;
  role: ColumnRole;
  type: ColumnType;
  /**
   * A `link` column's address, written with the row's own fields as `:name`
   * segments — `/deals/:dealId` (decision 0565). The cell is the column's own
   * text; the pattern is what it points at. A row missing a field the pattern
   * names draws the text alone, so a link is never half an address.
   */
  linkPattern?: string;
  /** 🔒 False where the header offers no sort (decision 10062): a report's running Balance, a figure in the order the report answered. */
  sortable?: boolean;
  /**
   * 🔒 The enum a `status` column is typed as, which the one status resolver
   * reads its label and colour by (decisions 0098, 0639). A status column
   * without it draws its stored word rather than a pill.
   */
  statusEntity?: string;
  /**
   * 🔒 The row field naming the approval of a `status` column's document (T1195;
   * decision 1861). A Draft the gate holds draws Pending Approval, and a rejected
   * one Rejected, in place of Draft; the stored status is untouched.
   */
  heldByKey?: string;
  /**
   * 🔒 A unit drawn after a number cell so a bare count reads as what it counts,
   * "4 sites" not "4" and "1 site" not "1 sites" (decision 1444; T691). Number
   * columns only; `countWithUnit` picks the form.
   */
  unit?: ColumnUnit;
  /**
   * 🔒 A column sized to its own content before the text columns take theirs
   * (decision 1444; T691): the part number column, cut off while others had room.
   * It takes its content's width in wide mode and never truncates.
   */
  fit?: boolean;
  /**
   * 🔒 The short name the Compact row's secondary line draws with this
   * column's figure, in the same text run (decision 1544; T813): "median 174.8
   * ms" where the Wide table's heading says Median (ms). `{value}` marks where
   * the figure stands, so a unit can follow it; a name without it stands before
   * the figure. A column without one draws its bare figure, and a column with a
   * `unit` draws the unit alone, never both. The Wide table never reads it.
   */
  compactLabel?: string;
  /**
   * 🔒 A secondary column whose figure must show whole on the Compact row (T3726; decision 12061): a time or a number
   * that would end in an ellipsis. A line carrying one wraps, as a line carrying a compact label does. The Wide table never reads it.
   */
  compactWrap?: boolean;
  /**
   * 🔒 A column the list offers but a view draws only once it is ticked in the view's columns (T3333; decision 9380):
   * the Items list's Thumbnail. A view naming no columns draws the list's set without it.
   */
  offByDefault?: boolean;
}

/** A number column's unit in both its forms, since English counts one thing apart from any other number. */
export interface ColumnUnit {
  one: string;
  other: string;
}

/**
 * 🔒 A figure and its unit, the singular for exactly one and the plural for
 * every other count, zero included (decision 1444): "1 site", "4 sites",
 * "0 sites". `figure` is the number as already formatted for display.
 */
export function countWithUnit(value: number, figure: string, unit: ColumnUnit): string {
  return `${figure} ${value === 1 ? unit.one : unit.other}`;
}

/** Where a compact label places the figure it names (decision 1544). */
export const COMPACT_LABEL_VALUE = '{value}';

/**
 * 🔒 One secondary figure as the Compact row draws it (decision 1544): named by
 * the column's compact label, or bare where the column carries none. A column
 * with a `unit` already reads as what it counts, so the unit wins and the label
 * is not drawn twice; an empty figure stays empty, so the line drops it.
 */
export function compactFigure(column: Pick<ColumnDef, 'compactLabel' | 'unit'>, figure: string): string {
  const label = column.compactLabel;
  if (!label || column.unit || figure === '') return figure;
  return label.includes(COMPACT_LABEL_VALUE) ? label.split(COMPACT_LABEL_VALUE).join(figure) : `${label} ${figure}`;
}

/**
 * A `link` column's address for one row, or null where the row does not carry
 * every field the pattern names. 🔨 Each value is encoded, because a row's
 * field is data and an address is not.
 *
 * 🔒 A pattern that is one field alone, `:entryRoute`, names a field holding a
 * whole address inside the application (decision 2771): Settings › History's
 * setting name, which links to a page rather than a record. It resolves only to
 * an address starting with one `/`, each of its segments encoded.
 */
export function resolveLinkPattern(pattern: string, row: Readonly<Record<string, unknown>>): string | null {
  if (/^:[^/]+$/.test(pattern)) {
    const route = row[pattern.slice(1)];
    if (typeof route !== 'string' || !route.startsWith('/') || route.startsWith('//')) return null;
    return route.split('/').map(encodeURIComponent).join('/');
  }
  const parts = pattern.split('/');
  const resolved: string[] = [];
  for (const part of parts) {
    if (!part.startsWith(':')) {
      resolved.push(part);
      continue;
    }
    const value = row[part.slice(1)];
    if (value === null || value === undefined || String(value) === '') return null;
    resolved.push(encodeURIComponent(String(value)));
  }
  return resolved.join('/');
}

export type Alignment = 'left' | 'right';

/** Value and number columns line up on the right; everything else reads from the left. */
export function alignmentFor(type: ColumnType): Alignment {
  return type === 'currency' || type === 'number' ? 'right' : 'left';
}

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  key: string;
  direction: SortDirection;
}

/** A repeat click on the active column flips the direction; a click elsewhere sorts ascending. */
export function toggleSort(current: SortState | null, key: string): SortState {
  if (current && current.key === key) return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  return { key, direction: 'asc' };
}

function compareValues(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  return String(a).localeCompare(String(b));
}

/** A total sort: equal values order by id, so a row never appears on two consecutive pages. */
export function sortRows<R>(rows: readonly R[], sort: SortState | null, read: (row: R, key: string) => unknown, idOf: (row: R) => string): R[] {
  const copy = [...rows];
  copy.sort((x, y) => {
    if (sort) {
      const c = compareValues(read(x, sort.key), read(y, sort.key));
      if (c !== 0) return sort.direction === 'asc' ? c : -c;
    }
    return idOf(x).localeCompare(idOf(y));
  });
  return copy;
}

export const PAGE_SIZES = [50, 100, 200] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
export const DEFAULT_PAGE_SIZE: PageSize = 50;

export function pageCount(total: number, size: PageSize): number {
  return Math.max(1, Math.ceil(total / size));
}

export function pageSlice<R>(rows: readonly R[], page: number, size: PageSize): R[] {
  const start = (page - 1) * size;
  return rows.slice(start, start + size);
}

/** The selection: the ids ticked on the page, or the view's whole filter minus exclusions, never a list of every id. */
export type Selection = { mode: 'ids'; ids: ReadonlySet<string> } | { mode: 'filter'; exclusions: ReadonlySet<string> };

export const emptySelection: Selection = { mode: 'ids', ids: new Set() };

export type SelectionEvent =
  | { type: 'toggleRow'; id: string }
  | { type: 'togglePage'; pageIds: readonly string[] }
  | { type: 'selectAllMatching' }
  | { type: 'clear' }
  | { type: 'viewSwitched' }
  | { type: 'navigated' };

export function selectionReducer(state: Selection, event: SelectionEvent): Selection {
  switch (event.type) {
    case 'toggleRow': {
      if (state.mode === 'filter') {
        const exclusions = new Set(state.exclusions);
        if (exclusions.has(event.id)) exclusions.delete(event.id);
        else exclusions.add(event.id);
        return { mode: 'filter', exclusions };
      }
      const ids = new Set(state.ids);
      if (ids.has(event.id)) ids.delete(event.id);
      else ids.add(event.id);
      return { mode: 'ids', ids };
    }
    case 'togglePage': {
      if (state.mode === 'filter') return emptySelection;
      const allOn = event.pageIds.every((id) => state.ids.has(id));
      const ids = new Set(state.ids);
      for (const id of event.pageIds) {
        if (allOn) ids.delete(id);
        else ids.add(id);
      }
      return { mode: 'ids', ids };
    }
    case 'selectAllMatching':
      return { mode: 'filter', exclusions: new Set() };
    case 'clear':
    case 'viewSwitched':
    case 'navigated':
      return emptySelection;
  }
}

export function isSelected(state: Selection, id: string): boolean {
  return state.mode === 'filter' ? !state.exclusions.has(id) : state.ids.has(id);
}

export function selectionCount(state: Selection, total: number): number {
  return state.mode === 'filter' ? Math.max(0, total - state.exclusions.size) : state.ids.size;
}

export function pageSelected(state: Selection, pageIds: readonly string[]): boolean {
  return pageIds.length > 0 && pageIds.every((id) => isSelected(state, id));
}

/** Select all N matching is offered once the page is selected and more pages exist. */
export function offersSelectAllMatching(state: Selection, pageIds: readonly string[], total: number): boolean {
  return state.mode === 'ids' && pageSelected(state, pageIds) && total > pageIds.length;
}

export type DisplayMode = 'wide' | 'compact';

export const LIST_VIEWER_COMPACT_BREAKPOINT = 480;

/** Compact switches on by measured width, never by a page's configuration. */
export function displayModeFor(measuredWidth: number, compactBreakpoint = LIST_VIEWER_COMPACT_BREAKPOINT): DisplayMode {
  return measuredWidth < compactBreakpoint ? 'compact' : 'wide';
}

/** Compact keeps two lines while the secondary line and the pill-and-value unit fit side by side, and earns a third only when they would collide. */
export function compactLineCount(secondaryWidth: number, pillValueWidth: number, availableWidth: number, gap = 8): 2 | 3 {
  return secondaryWidth + gap + pillValueWidth <= availableWidth ? 2 : 3;
}

export interface RowGroup<R> {
  key: string;
  rows: R[];
}

/** Groups in the caller's explicit order, never alphabetical; a value outside the order lands last, in first-seen order. */
export function groupRows<R>(rows: readonly R[], groupOf: (row: R) => string, order: readonly string[]): RowGroup<R>[] {
  const buckets = new Map<string, R[]>();
  for (const key of order) buckets.set(key, []);
  for (const row of rows) {
    const key = groupOf(row);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(row);
  }
  return [...buckets.entries()].map(([key, groupRows]) => ({ key, rows: groupRows }));
}

/** Wide mode truncates in left-to-right priority: the last secondary column gives first. */
export function truncationOrder(columns: readonly ColumnDef[]): string[] {
  return [...columns].reverse().filter((c) => c.role === 'secondary' || c.role === 'primary').map((c) => c.key);
}
